import { useEffect, useState } from "react";
import api from "@/lib/api";

/**
 * Admin-only soft-delete dialog for a project.
 * Luke must type the project name to enable the red Delete button, and if the
 * project has approved/paid financial records he must tick the override.
 *
 * props:
 *   project: { id, name }
 *   onClose(): void
 *   onDeleted(): void  — called after a successful soft delete
 */
export default function DeleteProjectDialog({ project, onClose, onDeleted }) {
  const [impact, setImpact] = useState(null);
  const [err, setErr] = useState("");
  const [typed, setTyped] = useState("");
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/projects/${project.id}/delete-impact`)
      .then(r => setImpact(r.data))
      .catch(e => setErr(e?.response?.data?.detail || "Could not load impact"));
  }, [project.id]);

  const matches = typed.trim() === (project.name || "").trim();
  const needsAck = impact?.has_financial_locks;
  const blocked = !!impact?.has_records;
  const canDelete = !blocked && matches && (!needsAck || ack) && !busy;

  async function doDelete() {
    setBusy(true); setErr("");
    try {
      await api.delete(`/projects/${project.id}`, { data: { financial_override: !!ack } });
      onDeleted();
    } catch (e) {
      const d = e?.response?.data?.detail;
      setErr(typeof d === "string" ? d : (d?.message || "Delete failed"));
    } finally { setBusy(false); }
  }

  const c = impact?.counts || {};
  return (
    <div className="fixed inset-0 bg-black/80 z-40 flex items-center justify-center p-4" data-testid="delete-project-dialog">
      <div className="card max-w-lg w-full">
        <h3 className="text-silver mb-2">Delete project</h3>
        <p className="text-sm opacity-80 mb-3">
          Soft-delete <b>{project.name}</b>? It will move to the Deleted bin and auto-purge after 30 days.
        </p>
        {!impact ? <div className="opacity-60 text-sm">Loading impact…</div> : (
          <ul className="text-xs opacity-80 space-y-0.5 mb-3">
            <li>• Stages: {c.stages ?? 0}</li>
            <li>• Tasks: {c.tasks ?? 0}</li>
            <li>• Estimates: {c.estimates ?? 0} · Estimate versions: {c.estimate_versions ?? 0}</li>
            <li>• Linked bills: {c.bills ?? 0} ({impact.approved_bills} approved/paid)</li>
            <li>• Timesheets: {c.timesheets ?? 0} ({impact.paid_timesheets} accepted/paid)</li>
            <li>• Variations: {c.variations ?? 0}</li>
            <li>• Purchase orders: {c.purchase_orders ?? 0}</li>
            <li>• Documents: {c.documents ?? 0}</li>
            <li>• Programme items: {c.programme_items ?? 0}</li>
          </ul>
        )}
        {blocked && (
          <div className="border border-amber-500/50 bg-amber-500/10 p-2 rounded text-sm text-amber-200 mb-3" data-testid="dp-has-records">
            This project has records. Complete or archive it instead.
          </div>
        )}
        {!blocked && impact?.has_financial_locks && (
          <div className="border border-red-500/50 bg-red-500/10 p-2 rounded text-xs text-red-300 mb-3">
            This project has <b>{impact.approved_bills}</b> approved/paid bill(s) and <b>{impact.paid_timesheets}</b> accepted/paid timesheet(s).
            <label className="flex items-center gap-2 mt-2 text-silver">
              <input type="checkbox" checked={ack} onChange={e=>setAck(e.target.checked)} data-testid="dp-ack-financial"/>
              I understand this removes financial records
            </label>
          </div>
        )}
        <label className="text-xs block">Type <b className="text-silver">{project.name}</b> to confirm
          <input className="input mt-1" value={typed} onChange={e=>setTyped(e.target.value)} data-testid="dp-typed-name"/>
        </label>
        {err && <div className="text-red-500 text-sm mt-2" data-testid="dp-error">{err}</div>}
        <div className="flex gap-2 mt-4">
          <button
            className={`px-3 py-1.5 rounded text-sm ${canDelete ? "bg-red-600 text-white" : "bg-neutral-700 text-neutral-400 cursor-not-allowed"}`}
            onClick={doDelete} disabled={!canDelete} data-testid="dp-confirm">
            {busy ? "Deleting…" : "Delete"}
          </button>
          <button className="btn-ghost" onClick={onClose} data-testid="dp-cancel">Cancel</button>
        </div>
      </div>
    </div>
  );
}
